import { __awaiter } from 'tslib';
import axios from '@anghami/axios';
import loadjs from 'loadjs';
import mitt from 'mitt';
import env from './env.js';
import { isServer, getCookie, uuid, getStringInRC4, setCookie } from './utils.js';
import postFingerPrint from './endpoints/postFingerPrint.js';
import { paramsToEncodedBody, processEncryption } from './encryption.js';

const curlirize = require('axios-curlirize');
class NeoGateway {
    constructor(gatewayConfig = {}) {
        this.authenticationResponse = null;
        this.tokens = {
            resKey: '',
            reqKey: '',
            socketsessionid: '',
            fingerprint: ''
        };
        // Same as EventEmitter but with much smaller size
        this.customResponseInterceptor = mitt();
        this.abortControllersMap = {};
        this.config = Object.assign({ encryption: true, recaptcha: true, autoloadSodium: true, curlirize: false, timeout: 25000 }, gatewayConfig);
        this.setTokens(Object.assign(Object.assign(Object.assign({}, this.tokens), gatewayConfig.tokens), { socketsessionid: this.config.appSid }));
        this.initGatewayCaller();
    }
    setTokens(tokens) {
        if (!tokens)
            return;
        this.tokens = Object.assign(Object.assign({}, this.tokens), tokens);
    }
    setSocketHeader(socketID) {
        this.gatewayCaller.defaults.headers['x-socket-id'] = socketID;
    }
    getContentTypeAndData(dataType = 'urlencoded', data = {}) {
        let contentType;
        let stringifiedData;
        if (dataType === 'urlencoded') {
            contentType = 'application/x-www-form-urlencoded';
            stringifiedData = paramsToEncodedBody(data);
        }
        else if (dataType === 'json') {
            stringifiedData = data;
            contentType = 'application/json';
        }
        else if (dataType === 'encrypted') {
            contentType = 'application/x-www-form-urlencoded';
            stringifiedData = data;
        }
        return [contentType, stringifiedData];
    }
    get(endpoint, params, customURL = '', customHeaders = {}, isKoussa = false) {
        return isKoussa
            ? this.getKoussa(endpoint, params, customURL, customHeaders)
            : this.getV1(endpoint, params, customURL, customHeaders);
    }
    post(endpoint, params, data = {}, customURL = '', customHeaders = {}, dataType = 'urlencoded', isKoussa = false) {
        return isKoussa
            ? this.postKoussa(endpoint, params, data, customURL, customHeaders, dataType)
            : this.postV1(endpoint, params, data, customURL, customHeaders, dataType);
    }
    encrypted(endpoint, params, data = {}, customURL = '', customHeaders = {}, isKoussa = false) {
        return isKoussa
            ? this.encryptedKoussa(endpoint, params, data, customURL, customHeaders)
            : this.encryptedV1(endpoint, params, data, customURL, customHeaders);
    }
    getKoussa(endpoint, params, customURL = '', customHeaders = {}) {
        // in case we want to add a staging custom url later
        return this.gatewayCaller.get('/' + endpoint, Object.assign(Object.assign({ baseURL: customURL || this.config.kousaApiUrl || env.KOUSA_API_URL }, (customHeaders && {
            headers: Object.assign(Object.assign({}, this.gatewayCaller.defaults.headers), customHeaders)
        })), { params }));
    }
    getV1(endpoint, params, customURL = '', customHeaders = {}) {
        // in case we want to add a staging custom url later
        return this.gatewayCaller.get('', Object.assign(Object.assign(Object.assign({}, (customURL && { baseURL: customURL })), (customHeaders && {
            headers: Object.assign(Object.assign({}, this.gatewayCaller.defaults.headers), customHeaders)
        })), { params: Object.assign(Object.assign({}, params), { type: endpoint }) }));
    }
    postKoussa(endpoint, params, data = {}, customURL = '', customHeaders = {}, dataType = 'urlencoded') {
        const [contentType, stringifiedData] = this.getContentTypeAndData(dataType, data);
        return this.gatewayCaller.post('/' + endpoint, stringifiedData, {
            baseURL: customURL || this.config.kousaApiUrl || env.KOUSA_API_URL,
            headers: Object.assign(Object.assign(Object.assign({}, this.gatewayCaller.defaults.headers), customHeaders), { 'Content-Type': contentType }),
            params
        });
    }
    deleteKoussa(endpoint, params, data = {}, customURL = '', customHeaders = {}, dataType = 'urlencoded') {
        const [contentType] = this.getContentTypeAndData(dataType, data);
        return this.gatewayCaller.delete('/' + endpoint, {
            baseURL: customURL || this.config.kousaApiUrl || env.KOUSA_API_URL,
            headers: Object.assign(Object.assign(Object.assign({}, this.gatewayCaller.defaults.headers), customHeaders), { 'Content-Type': contentType }),
            params
        });
    }
    postV1(endpoint, params, data = {}, customURL = '', customHeaders = {}, dataType = 'urlencoded') {
        const [contentType, stringifiedData] = this.getContentTypeAndData(dataType, data);
        return this.gatewayCaller.post('', stringifiedData, Object.assign(Object.assign({}, (customURL && { baseURL: customURL })), { headers: Object.assign(Object.assign(Object.assign({}, this.gatewayCaller.defaults.headers), customHeaders), { 'Content-Type': contentType }), params: Object.assign(Object.assign({}, params), { type: endpoint }) }));
    }
    encryptedKoussa(endpoint, params, data = {}, customURL = '', customHeaders = {}) {
        var _a;
        // to prevent encrypting auth request using res/req keys
        const isAuth = endpoint.indexOf('authenticate') !== -1;
        const tsInSeconds = Math.floor(Date.now() / 1000);
        const encryptedBody = processEncryption(this.config.encryption, this.tokens.fingerprint, true, data, tsInSeconds, isAuth ? '' : this.tokens.reqKey);
        return this.post(endpoint, Object.assign(Object.assign({}, params), { 'ngsw-bypass': true }), encryptedBody, customURL, Object.assign(Object.assign({}, customHeaders), { 'X-ANGH-SESSION': this.tokens.socketsessionid, 'X-ANGH-ENCPAYLOAD': '5', 'X-ANGH-TS': tsInSeconds.toString(), 'X-ANGH-UDID': (_a = this.tokens.fingerprint) === null || _a === void 0 ? void 0 : _a.toLowerCase() }), 'encrypted', true).then((response) => {
            const decryptedData = processEncryption(this.config.encryption, this.tokens.fingerprint, false, response.data, tsInSeconds, isAuth ? '' : this.tokens.resKey);
            decryptedData.responsetype = endpoint;
            response.data = decryptedData;
            this.customResponseInterceptor.emit('api_response_success', response);
            return response;
        });
    }
    encryptedV1(endpoint, params, data = {}, customURL = '', customHeaders = {}) {
        var _a;
        // to prevent encrypting auth request using res/req keys
        const isAuth = endpoint.indexOf('authenticate') !== -1;
        const tsInSeconds = Math.floor(Date.now() / 1000);
        const encryptedBody = processEncryption(this.config.encryption, this.tokens.fingerprint, true, data, tsInSeconds, isAuth ? '' : this.tokens.reqKey);
        return this.post(endpoint, Object.assign(Object.assign({}, params), { 'ngsw-bypass': true }), encryptedBody, customURL, Object.assign(Object.assign({}, customHeaders), { 'X-ANGH-SESSION': this.tokens.socketsessionid, 'X-ANGH-ENCPAYLOAD': '3', 'X-ANGH-TS': tsInSeconds.toString(), 'X-ANGH-UDID': (_a = this.tokens.fingerprint) === null || _a === void 0 ? void 0 : _a.toLowerCase() }), 'encrypted', false).then((response) => {
            // Reject the endpoint Promise if it does not have the reply data
            var _a;
            if (!((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.reply)) {
                return Promise.reject(response);
            }
            const decryptedData = processEncryption(this.config.encryption, this.tokens.fingerprint, false, response.data, tsInSeconds, isAuth ? '' : this.tokens.resKey);
            decryptedData.responsetype = endpoint;
            response.data = decryptedData;
            this.customResponseInterceptor.emit('api_response_success', response);
            return response;
        });
    }
    loadSodium(altURL = false) {
        return new Promise((resolve, reject) => {
            const sodiumURL = altURL ? env.SODIUM_CDN_URL_ALT : env.SODIUM_CDN_URL;
            const fflateURL = altURL ? env.FFLATE_CDN_URL_ALT : env.FFLATE_CDN_URL;
            if (!isServer) {
                loadjs([sodiumURL, fflateURL], {
                    numRetries: 2,
                    success: () => __awaiter(this, void 0, void 0, function* () {
                        const sodium = window.sodium;
                        sodium.ready
                            .then(() => {
                            resolve();
                        })
                            .catch(() => {
                            reject(new Error('Error calling ready on libsodium in browser'));
                        });
                    }),
                    error: reject
                });
            }
            else {
                // crazy hack to prevent @angular/cli from bundling
                // the libsodium in the browser js files
                // eslint-disable-next-line no-eval
                eval("(global as any).sodium = require('libsodium-wrappers')");
                // eslint-disable-next-line no-eval
                eval("(global as any).fflate = require('fflate')");
                const sodium = global.sodium;
                sodium.ready
                    .then(() => {
                    resolve();
                })
                    .catch(() => {
                    reject(new Error('Error calling ready on libsodium in node'));
                });
            }
        });
    }
    initGatewayCaller() {
        var _a;
        const lang = 'en';
        let apiURL = this.config.apiURL || env.API_URL;
        // To use custom API url from env
        if (this.config.catchURLFromEnv && typeof process !== 'undefined') {
            const url = process.env[this.config.apiUrlEnvName];
            if (url)
                apiURL = url;
        }
        // For nodejs environment we need to force it to use the http module instead of xhr
        const axiosAdapter = isServer
            ? require('@anghami/axios/lib/adapters/http')
            : axios.defaults.adapter;
        this.gatewayCaller = axios.create({
            baseURL: apiURL,
            withCredentials: true,
            timeout: (_a = this.config) === null || _a === void 0 ? void 0 : _a.timeout,
            adapter: axiosAdapter,
            headers: Object.assign({ 'Content-Type': 'application/x-www-form-urlencoded' }, (this.config.customHeaders || {})),
            params: Object.assign(Object.assign(Object.assign({ language: lang }, (this.tokens.socketsessionid && { appsid: this.tokens.socketsessionid })), { web2: true }), (this.config.customParams || {}))
        });
        this.gatewayCaller.defaults.withCredentials = true; // To send the cookies
        this.gatewayCaller.interceptors.response.use((response) => this.successHandler(response), (error) => this.errorHandler(error));
        this.gatewayCaller.interceptors.request.use((config) => {
            var _a;
            // Do something before request is sent
            // append default params
            // TODO: add interceptors midleware
            // we should not send jwt on authenticate
            const shouldSendJWT = this.tokens.jwtToken && config.params.type !== 'authenticate';
            config.headers = Object.assign(Object.assign(Object.assign({}, config.headers), (config.params.cookies && { cookies: config.params.cookies })), (shouldSendJWT && { Authorization: 'Bearer ' + this.tokens.jwtToken }));
            config.params = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, config.params), this.gatewayCaller.defaults.params), { fingerprint: this.tokens.fingerprint }), (this.tokens.socketsessionid && {
                sid: this.tokens.socketsessionid,
                appsid: this.tokens.socketsessionid
            })), { angh_type: config.params.type });
            // passing the abortSignal to axios to set the cancellation status
            if ((_a = config === null || config === void 0 ? void 0 : config.params) === null || _a === void 0 ? void 0 : _a.abortSignal) {
                config.signal = config.params.abortSignal;
            }
            return config;
        }, (error) => {
            return Promise.reject(error);
        });
        // enable requests logging in cURL format
        if (this.config.curlirize) {
            curlirize(this.gatewayCaller, (result) => {
                console.log('\n\n');
                console.log(result.command);
                console.log('\n\n');
            });
        }
    }
    successHandler(response) {
        var _a;
        const isEncrypted = Boolean((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.reply);
        // encrypted endpoints will emit this event after they get decrypted
        if (!isEncrypted) {
            this.customResponseInterceptor.emit('api_response_success', response);
        }
        return response;
    }
    errorHandler(errorResponse) {
        this.customResponseInterceptor.emit('api_response_error', errorResponse);
        return errorResponse;
    }
    initFingerprint() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const fingerPrint = getCookie('fingerprint');
            if (fingerPrint && fingerPrint !== 'undefined')
                return fingerPrint;
            if (this.tokens.fingerprint)
                return this.tokens.fingerprint;
            const fp = uuid();
            // replacing = character with a random 1, just to avoid issues on api
            const enFingerPrint64 = btoa(getStringInRC4(fp)).replace('=', '1');
            try {
                // Post the fingerprint to the APIs
                const response = yield this.callEndpoint(postFingerPrint, {
                    fp,
                    hash: enFingerPrint64
                });
                if (!response.data)
                    return '';
                // also here replacing = character with a random 1, cause fingerprint is sent on auth as uuid from cookies
                const inFingerprint = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.fingerprint) === null || _b === void 0 ? void 0 : _b.replace('=', '1');
                setCookie('fingerprint', inFingerprint, 360);
                return inFingerprint;
            }
            catch (_) {
                return '';
            }
        });
    }
    ready() {
        if (typeof TextEncoder === 'undefined') {
            loadjs(env.TEXT_ENCODER_POLYFILL, 'encoderPolyfill');
            loadjs.ready('encoderPolyfill', {
                success: () => { },
                error: () => {
                    loadjs(env.TEXT_ENCODER_POLYFILL_ALT, () => { });
                }
            });
        }
        // Check for nodejs env and load the node polyfills
        if (typeof process === 'object') {
            global.btoa = require('btoa');
            global.atob = require('atob');
        }
        return new Promise((resolve, reject) => {
            // Load sodium as soon as the package inited
            if (this.config.encryption && this.config.autoloadSodium) {
                this.loadSodium()
                    .catch(() => __awaiter(this, void 0, void 0, function* () { return this.loadSodium(true); })) // fall to the ALT src
                    .then(() => __awaiter(this, void 0, void 0, function* () {
                    // Load sodium as soon as the package inited
                    this.setTokens(Object.assign(Object.assign({}, this.tokens), { fingerprint: yield this.initFingerprint() }));
                    resolve();
                }))
                    .catch((e) => {
                    reject(e);
                });
            }
            else {
                this.initFingerprint().then((fingerprint) => {
                    this.setTokens(Object.assign(Object.assign({}, this.tokens), { fingerprint }));
                    resolve();
                });
            }
        });
    }
    callEndpoint(endpoint, params = {}, isKoussa = false, customURL = null, customHeaders = {}) {
        // For canceling requests manually by calling this.cancelCallEndpoint method and passing the abortControllerKey
        if (params === null || params === void 0 ? void 0 : params.abortControllerKey) {
            const controller = new AbortController();
            params.abortSignal = controller.signal;
            this.abortControllersMap[params.abortControllerKey] = controller;
        }
        return endpoint.bind(endpoint)(this, params, customURL, customHeaders, isKoussa);
    }
    cancelCallEndpoint(abortControllerKey) {
        if (this.abortControllersMap[abortControllerKey]) {
            this.abortControllersMap[abortControllerKey].abort();
        }
    }
}

export { NeoGateway as default };
//# sourceMappingURL=index.js.map
